import { Address, zeroAddress } from 'viem'
import { useChainId, useReadContract, useReadContracts } from 'wagmi'
import { FRAXLEND_PAIR_REGISTRY_ABI } from '@/config/abis/FraxlendPairRegistry.abi'
import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { Token } from '@/domain/types/Token'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useChainConfigEntry } from '@/domain/hooks/useChainConfigEntry'
import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { FRAX_ORACLE_ABI } from '@/config/abis/fraxOracleAbi.abi'
import { MarketStats } from '@/features/markets/logic/aggregate-stats'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useMerklOpportunities } from '@/domain/merkl/opportunities/useOpportunities'
import { Opportunity } from '@merkl/api'

const SECONDS_PER_YEAR = 31_556_952

const ERC20_ABI = [
  { inputs: [], name: 'decimals', outputs: [{ type: 'uint8' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
] as const

export type PairDetails = {
  address: Address
  collateralToken: Token
  borrowToken: Token
  totalCollateral: bigint
  totalBorrow: bigint
  available: bigint
  utilization: number
  borrowAPY: number
  lendAPY: number
  decimals: number
  merklOpportunity?: Opportunity
}

export function useIsolatedPairs(): {
  pairs: PairDetails[]
  marketStats: MarketStats
  isLoading: boolean
} {
  const { isolated } = useChainConfigEntry()
  const { findByIdentifier } = useMerklOpportunities()
  const chainId = useChainId()
  const { marketInfo } = useMarketInfo({ chainId, refetchInterval: QUERY_REFETCH_INTERVAL })

  const { data: pairAddresses, isLoading: isLoadingAddresses } = useReadContract({
    address: isolated?.registryAddress,
    abi: FRAXLEND_PAIR_REGISTRY_ABI,
    functionName: 'getAllPairAddresses',
  })

  const accountingReads = pairAddresses?.map((address) => ({
    address: address as Address,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'getPairAccounting',
  }))

  const rateInfoReads = pairAddresses?.map((address) => ({
    address: address as Address,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'currentRateInfo',
  }))

  const decimalsReads = pairAddresses?.map((address) => ({
    address: address as Address,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'decimals',
  }))

  const collateralContractReads = pairAddresses?.map((address) => ({
    address: address as Address,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'collateralContract',
  }))

  const assetContractReads = pairAddresses?.map((address) => ({
    address: address as Address,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'asset',
  }))

  const { data: accountingDetails, isLoading: isLoadingAccounting } = useReadContracts({
    contracts: accountingReads,
  }) as { data: any[]; isLoading: boolean }

  const { data: rateInfoDetails, isLoading: isLoadingRateInfo } = useReadContracts({
    contracts: rateInfoReads,
  }) as { data: any[]; isLoading: boolean }

  const { data: decimalsDetails, isLoading: isLoadingDecimals } = useReadContracts({
    contracts: decimalsReads,
  }) as { data: any[]; isLoading: boolean }

  const { data: collateralContractDetails, isLoading: isLoadingCollateralContract } = useReadContracts({
    contracts: collateralContractReads,
  }) as { data: any[]; isLoading: boolean }

  const { data: assetContractDetails, isLoading: isLoadingAssetContract } = useReadContracts({
    contracts: assetContractReads,
  }) as { data: any[]; isLoading: boolean }

  // Get token decimals, symbols and names for all pairs
  const tokenInfoReads = pairAddresses?.flatMap((_, index) => {
    const collateralAddress = collateralContractDetails?.[index]?.result as Address
    const borrowAddress = assetContractDetails?.[index]?.result as Address
    return [
      {
        address: collateralAddress,
        abi: ERC20_ABI,
        functionName: 'decimals' as const,
      },
      {
        address: collateralAddress,
        abi: ERC20_ABI,
        functionName: 'symbol' as const,
      },
      {
        address: collateralAddress,
        abi: ERC20_ABI,
        functionName: 'name' as const,
      },
      {
        address: borrowAddress,
        abi: ERC20_ABI,
        functionName: 'decimals' as const,
      },
      {
        address: borrowAddress,
        abi: ERC20_ABI,
        functionName: 'symbol' as const,
      },
      {
        address: borrowAddress,
        abi: ERC20_ABI,
        functionName: 'name' as const,
      },
    ]
  })

  const { data: tokenInfos, isLoading: isLoadingTokenInfos } = useReadContracts({
    contracts: tokenInfoReads,
  }) as { data: any[]; isLoading: boolean }

  const exchangeRateInfoReads =
    pairAddresses?.map((pair) => ({
      address: pair as Address,
      abi: FRAXLEND_PAIR_ABI,
      functionName: 'exchangeRateInfo',
    })) || []

  const { data: exchangeRateInfos = [], isLoading: isLoadingExchangeRateInfos } = useReadContracts({
    contracts: exchangeRateInfoReads,
  }) as { data: { result?: unknown }[]; isLoading: boolean }

  const directOraclePricesReads =
    exchangeRateInfos?.map((exchangeRate) => {
      // Use a type assertion that doesn't cause infinite type instantiation
      const exchangeRateInfo = exchangeRate?.result as [Address, bigint, bigint, bigint, bigint] | undefined

      return {
        address: exchangeRateInfo?.[0] || zeroAddress,
        abi: FRAX_ORACLE_ABI,
        functionName: 'getPrices',
      }
    }) || []

  const { data: directOraclePricesInfos, isLoading: isLoadingDirectOraclePricesInfos } = useReadContracts({
    contracts: directOraclePricesReads,
  })

  const constantReads =
    pairAddresses?.map((pair) => ({
      address: pair as Address,
      abi: FRAXLEND_PAIR_ABI,
      functionName: 'getConstants',
    })) || []

  const { data: constantInfos } = useReadContracts({
    contracts: constantReads,
  }) as { data: any[]; isLoading: boolean }

  const pairs: PairDetails[] = pairAddresses
    ? pairAddresses.map((address: Address, index: number) => {
        const accountingResult = accountingDetails?.[index]?.result as
          | [bigint, bigint, bigint, bigint, bigint]
          | undefined
        const rateInfoResult = rateInfoDetails?.[index]?.result as [number, number, bigint, bigint, bigint] | undefined
        const decimalsResult = decimalsDetails?.[index]?.result as number | undefined
        const collateralContractResult = collateralContractDetails?.[index]?.result as Address | undefined
        const assetContractResult = assetContractDetails?.[index]?.result as Address | undefined

        const totalAssetAmount = accountingResult?.[0] || 0n
        const totalAssetShares = accountingResult?.[1] || 0n
        const totalBorrow = accountingResult?.[2] || 0n
        const totalBorrowShares = accountingResult?.[3] || 0n
        const totalCollateral = accountingResult?.[4] || 0n

        const available = totalAssetAmount - totalBorrow
        const utilization =
          totalAssetAmount > 0n ? Number((totalBorrowShares * 1000000n) / totalAssetShares) / 10000 : 0

        // Calculate APY using rate info
        const ratePerSec = rateInfoResult?.[3] ? Number(rateInfoResult[3]) / 1e18 : 0
        // feeToProtocolRate is 1e5 precision
        // see: https://github.com/FraxFinance/fraxlend-audit/blob/76c537d429ea4ac9bed2b13fc2cca90cccb2791a/src/contracts/FraxlendPairCore.sol#L103
        const feeToProtocolRate = rateInfoResult?.[1] ? Number(rateInfoResult[1]) / 1e5 : 0

        // Compute Borrow APY using continuous compounding formula
        const borrowAPY = ratePerSec > 0 ? (Math.exp(ratePerSec * SECONDS_PER_YEAR) - 1) * 100 : 0

        // Compute Lend APY after protocol fee
        const lendAPY = borrowAPY * (utilization / 100) * (1 - feeToProtocolRate)

        // Get decimals from contract
        const decimals = decimalsResult || 18 // Default to 18 if not available

        // Get token info
        const collateralDecimals = tokenInfos?.[index * 6]?.result as number
        const collateralSymbol = tokenInfos?.[index * 6 + 1]?.result as string
        const collateralName = tokenInfos?.[index * 6 + 2]?.result as string
        const borrowDecimals = tokenInfos?.[index * 6 + 3]?.result as number
        const borrowSymbol = tokenInfos?.[index * 6 + 4]?.result as string
        const borrowName = tokenInfos?.[index * 6 + 5]?.result as string
        const exchangeRateInfo = exchangeRateInfos?.[index]?.result as
          | readonly [Address, bigint, bigint, bigint, bigint]
          | undefined

        const directOraclePrices = directOraclePricesInfos?.[index]?.result as [boolean, bigint, bigint] | undefined
        const constants = constantInfos?.[index]?.result as
          | readonly [bigint, bigint, bigint, bigint, bigint, bigint, bigint, bigint]
          | undefined

        // Extract oracle info
        const [_oracleAddress, _maxOracleDeviation, _oracleLastTimestamp, lowExchangeRate, _highExchangeRate] =
          exchangeRateInfo || []

        const collateralReserveToken = marketInfo.reserves.find((r) => r.token.address === collateralContractResult)
        const borrowReserveToken = marketInfo.reserves.find((r) => r.token.address === assetContractResult)

        let collateralPrice = collateralReserveToken?.priceInUSD.toString() || '0'
        let borrowPrice = borrowReserveToken?.priceInUSD.toString() || '0'

        const [
          _LTV_PRECISION,
          _LIQ_PRECISION,
          _UTIL_PREC,
          _FEE_PRECISION,
          _EXCHANGE_PRECISION,
          _DEVIATION_PRECISION,
          _RATE_PRECISION,
          _MAX_PROTOCOL_FEE,
        ] = constants || []

        const currentExchangeRate =
          (Number(directOraclePrices ? Number(directOraclePrices[1]) : Number(lowExchangeRate)) /
            Number(_EXCHANGE_PRECISION)) *
          10 ** (borrowDecimals - collateralDecimals)

        if (collateralPrice === '0' && borrowPrice !== '0') {
          // Only update if the result is a valid number
          const calculatedPrice = Number(borrowPrice) * Number(1 / currentExchangeRate)
          collateralPrice = !Number.isNaN(calculatedPrice) ? calculatedPrice.toString() : '0'
        } else if (borrowPrice === '0' && collateralPrice !== '0') {
          // Only update if the result is a valid number
          const calculatedPrice = Number(collateralPrice) / currentExchangeRate
          borrowPrice = !Number.isNaN(calculatedPrice) ? calculatedPrice.toString() : '0'
        } else if (collateralPrice === '0' && borrowPrice === '0') {
          collateralPrice = '0'
          borrowPrice = '0'
        }

        if (Number.isNaN(Number(collateralPrice))) collateralPrice = '0'
        if (Number.isNaN(Number(borrowPrice))) borrowPrice = '0'

        return {
          address,
          collateralToken: new Token({
            address: CheckedAddress(collateralContractResult || '0x0000000000000000000000000000000000000000'),
            symbol: TokenSymbol(collateralSymbol || 'e'),
            name: collateralName || collateralSymbol || '',
            decimals: collateralDecimals || 18,
            unitPriceUsd: collateralPrice,
          }),
          borrowToken: new Token({
            address: CheckedAddress(assetContractResult || '0x0000000000000000000000000000000000000000'),
            symbol: TokenSymbol(borrowSymbol || 'e'),
            name: borrowName || borrowSymbol || '',
            decimals: borrowDecimals || 18,
            unitPriceUsd: borrowPrice,
          }),
          totalCollateral,
          totalBorrow,
          available,
          utilization,
          borrowAPY,
          lendAPY,
          decimals,
          merklOpportunity: findByIdentifier(CheckedAddress(address)),
        }
      })
    : []

  const marketStats = pairs?.reduce(
    (acc, pair) => {
      const lendAvailableValueUSD =
        (Number(pair.borrowToken.unitPriceUsd) * Number(pair.available)) / 10 ** pair.borrowToken.decimals
      const collateralValueUSD =
        (Number(pair.collateralToken.unitPriceUsd) * Number(pair.totalCollateral)) / 10 ** pair.collateralToken.decimals
      const borrowValueUSD =
        (Number(pair.borrowToken.unitPriceUsd) * Number(pair.totalBorrow)) / 10 ** pair.borrowToken.decimals
      return {
        totalMarketSizeUSD: NormalizedUnitNumber(
          acc.totalMarketSizeUSD.plus(
            NormalizedUnitNumber(lendAvailableValueUSD + borrowValueUSD + collateralValueUSD),
          ),
        ),
        totalValueLockedUSD: NormalizedUnitNumber(
          acc.totalValueLockedUSD.plus(NormalizedUnitNumber(collateralValueUSD)),
        ),
        totalAvailableUSD: NormalizedUnitNumber(
          acc.totalAvailableUSD.plus(NormalizedUnitNumber(lendAvailableValueUSD)),
        ),
        totalBorrowsUSD: NormalizedUnitNumber(acc.totalBorrowsUSD.plus(NormalizedUnitNumber(borrowValueUSD))),
      }
    },
    {
      totalMarketSizeUSD: NormalizedUnitNumber(0),
      totalValueLockedUSD: NormalizedUnitNumber(0),
      totalAvailableUSD: NormalizedUnitNumber(0),
      totalBorrowsUSD: NormalizedUnitNumber(0),
    },
  )

  return {
    pairs,
    marketStats,
    isLoading:
      isLoadingAddresses ||
      isLoadingAccounting ||
      isLoadingRateInfo ||
      isLoadingDecimals ||
      isLoadingCollateralContract ||
      isLoadingAssetContract ||
      isLoadingTokenInfos ||
      isLoadingExchangeRateInfos ||
      isLoadingDirectOraclePricesInfos,
  }
}
